import {memo} from "react";
import {Box, HStack, Icon} from "@chakra-ui/react";
import {ChatBubbleLeftIcon} from "@heroicons/react/16/solid";
import {Button} from "../../../../../components/ui/button";
import type {MockChatMessage, MockReviewStatus, SignOffs} from "../types";
import {MockAvatar} from "./MockAvatar";

const REVIEW_BORDER_COLORS: Record<MockReviewStatus, string> = {
  approved: "green.500",
  pending: "yellow.400",
  "changes-requested": "red.500",
  commented: "blue.500",
};

export const ReviewsWidget = memo(({signOffs, onOpen}: {signOffs: SignOffs; onOpen: () => void}) => {
  const roles = ["preparer", "reviewer", "manager"] as const;
  const additionalEntries = signOffs.additional;
  const totalSlots = roles.length + additionalEntries.length;
  const assignedCount = roles.filter(r => signOffs[r]).length + additionalEntries.length;

  return (
    <Button size="xs" variant="outline" colorPalette="gray" bg="white" onClick={onOpen}>
      <HStack gap={0}>
        {roles.map((role, i) => {
          const entry = signOffs[role];
          if (entry) {
            return (
              <Box key={role} ml={i > 0 ? "-4px" : 0} position="relative" zIndex={totalSlots - i}>
                <MockAvatar
                  name={entry.signer}
                  size="2xs"
                  w="16px"
                  h="16px"
                  css={{
                    boxShadow: `0 0 0 1px white, 0 0 0 3.5px var(--ring-color)`,
                    "--ring-color": `var(--chakra-colors-${REVIEW_BORDER_COLORS[entry.status].replace(".", "-")})`,
                  }}
                />
              </Box>
            );
          }
          return (
            <Box
              key={role}
              ml={i > 0 ? "-4px" : 0}
              position="relative"
              zIndex={totalSlots - i}
              w="16px"
              h="16px"
              borderRadius="full"
              border="1.5px dashed {colors.gray.300}"
              bg="white"
            />
          );
        })}
        {additionalEntries.map(({id, signer, status}, i) => {
          const idx = roles.length + i;
          return (
            <Box key={id} ml="-4px" position="relative" zIndex={totalSlots - idx}>
              <MockAvatar
                name={signer}
                size="2xs"
                w="16px"
                h="16px"
                css={{
                  boxShadow: `0 0 0 1px white, 0 0 0 3.5px var(--ring-color)`,
                  "--ring-color": `var(--chakra-colors-${REVIEW_BORDER_COLORS[status].replace(".", "-")})`,
                }}
              />
            </Box>
          );
        })}
      </HStack>
      {assignedCount}/{totalSlots} reviews
    </Button>
  );
});

export const CommentsWidget = memo(
  ({messages, unreadCount, onOpen}: {messages: MockChatMessage[]; unreadCount: number; onOpen: () => void}) => (
    <Button
      size="xs"
      variant={unreadCount > 0 ? "solid" : "outline"}
      colorPalette={unreadCount > 0 ? "orange" : "gray"}
      bg={unreadCount > 0 ? undefined : "white"}
      onClick={onOpen}
    >
      <Icon asChild boxSize="3.5">
        <ChatBubbleLeftIcon />
      </Icon>
      {unreadCount > 0 ? `${unreadCount} unread` : `${messages.length} comment${messages.length !== 1 ? "s" : ""}`}
    </Button>
  ),
);
